"use client";

import React, { useEffect, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import telegram_image from "../../assets/telegram-analytics.png";
import instagram_image from "../../assets/instagram-analytics.png";
import whatsapp_image from "../../assets/whatsapp-analytics.png";
import facebook_image from "../../assets/facebook-analytics.png";
import mail_image from "../../assets/mail-analytics.png";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function ClientsBySource() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("Month");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    const fetchClientsBySource = async () => {
      try {
        setLoading(true);
        setError(null);

        const headers = buildHeaders(token);
        
        const res = await fetch(`${API_URL}/analytics/best-services/`, {
          headers,
        });

        if (res.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const clients = data?.clients || {};

        const formatted = [
          {
            name: "Instagram",
            percent: clients.instagram ?? 0,
            image: instagram_image,
          },
          {
            name: "WhatsApp",
            percent: clients.whatsapp ?? 0,
            image: whatsapp_image,
          },
          {
            name: "Telegram",
            percent: clients.telegram ?? 0,
            image: telegram_image,
          },
          {
            name: "Facebook",
            percent: clients.facebook ?? 0,
            image: facebook_image,
          },
          {
            name: "Email",
            percent: clients.email ?? 0,
            image: mail_image,
          },
        ];

        setData(formatted);
      } catch (err) {
        console.error("❌ Failed to fetch client sources:", err);
        setError("Failed to load client sources data.");
      } finally {
        setLoading(false);
      }
    };

    fetchClientsBySource();
  }, [token, navigate]);

  return (
    <div className="bg-white rounded-2xl shadow-md p-5 w-full">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-[#302B2B] text-xl font-bold">Clients by source</h2>

        <div className="relative">
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 border border-[#9A99AB] text-[#9A99AB] text-sm rounded-xl px-4 py-2"
          >
            By {mode}
            <ChevronDownIcon
              className={`w-4 h-4 transform transition ${
                open ? "rotate-180" : ""
              }`}
            />
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-32 rounded-lg bg-white shadow-md border border-neutral-200 overflow-hidden z-10">
              {["Day", "Month"].map((opt) => (
                <div
                  key={opt}
                  onClick={() => {
                    setMode(opt);
                    setOpen(false);
                  }}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-indigo-50 ${
                    mode === opt
                      ? "text-indigo-600 font-medium"
                      : "text-neutral-700"
                  }`}
                >
                  By {opt}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="text-red-500 text-sm mb-4 italic">{error}</p>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-[250px] text-neutral-400">
          Loading sources...
        </div>
      ) : (
        <div className="flex flex-col gap-12">
          {data.map((src, i) => (
            <div
              key={i}
              className="flex items-center justify-between w-full h-[30px] gap-3"
            >
              <div className="flex-1 h-[30px] bg-[#F6F6F6] rounded-full relative overflow-hidden">
                <div
                  className="absolute left-0 top-0 h-full bg-[#5563F6] rounded-full transition-all duration-500"
                  style={{ width: `${src.percent}%` }}
                ></div>
              </div>

              <div className="flex items-center gap-2 min-w-[60px] justify-between w-[77px]">
                <div className="w-[30px] h-[30px] flex items-center justify-center">
                  <img
                    src={src.image}
                    alt={src.name}
                    className="w-[24px] h-[24px] object-contain"
                  />
                </div>
                <span className="text-[#302B2B] text-base font-normal">
                  {src.percent}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}